import {selectProjectData, selectTheme} from '../../store'
import {PopupSize, PopupTemplate, Tag} from '..'
import {useCurrentWorkspaceState} from '../../engine'
import {IQuestionnaire, ProjectStatus} from '../../shared/db'
import {useMemo} from 'react'

export const PopupDuplicateExistingQuestionnaire = (props: {
  setRenderDuplicateExistingQuestionnairePopup: (value: boolean) => void
  questionnaireList: IQuestionnaire[]
  createQuestionnaireDraftFromOtherQuestionnaire: (questionnaire: IQuestionnaire) => void
}) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const {
    setRenderDuplicateExistingQuestionnairePopup,
    questionnaireList,
    createQuestionnaireDraftFromOtherQuestionnaire,
  } = props
  const projectData = selectProjectData()
  const content = useMemo(() => {
    const result = questionnaireList.reduce<Record<string, IQuestionnaire[]>>((acc, questionnaire) => {
      if (!acc[questionnaire.projectId]) {
        acc[questionnaire.projectId] = []
      }
      acc[questionnaire.projectId].push(questionnaire)
      return acc
    }, {})
    Object.keys(result).forEach((key) => {
      const project = projectData[key]
      if (!project || ![ProjectStatus.Draft, ProjectStatus.Live].includes(project.status)) {
        delete result[key]
      }
    })
    return result
  }, [questionnaireList, projectData])

  const handleClick = (questionnaire: IQuestionnaire) => {
    createQuestionnaireDraftFromOtherQuestionnaire(questionnaire)
    setRenderDuplicateExistingQuestionnairePopup(false)
  }

  return (
    <PopupTemplate
      {...{
        title: 'Select an existing questionnaire',
        description:
          "Choose a questionnaire from the list below to duplicate into this task. You'll be able to edit it freely without affecting the original version.",
        background: true,
        popupSize: PopupSize.Big,
        hideBottomPart: true,
        handleClosePopup: () => {
          setRenderDuplicateExistingQuestionnairePopup(false)
        },
        centerPartBackgroundColor: color.surface.default,
      }}>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 40,
        }}>
        {Object.entries(content).map(([projectId, qList]) => {
          const project = projectData[projectId]
          return (
            <div
              key={projectId}
              css={{
                width: '100%',
                height: '100%',
                overflow: 'scroll',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                <span
                  css={{
                    textTransform: 'capitalize',
                    padding: '2px 6px',
                    borderRadius: 2,
                    backgroundColor:
                      project.status === ProjectStatus.Live ? color.tag.green.light : color.tag.grey.light,
                    color: project.status === ProjectStatus.Live ? color.tag.green.dark : color.tag.grey.dark,
                  }}>
                  {project.status}
                </span>
                <span
                  css={{
                    fontSize: fontSize.h4,
                    fontWeight: fontWeight.bold,
                  }}>
                  {project.name}
                </span>
              </div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}>
                {qList.map((q) => {
                  return (
                    <button
                      key={q.id}
                      onClick={() => handleClick(q)}
                      css={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: fontWeight.bold,
                        padding: 16,
                        borderRadius: 5,
                        border: `1px solid ${color.border.default}`,
                        backgroundColor: color.surface.white,
                        cursor: 'pointer',
                        ':hover': {
                          backgroundColor: color.surface.hover.hoverWhiteBg,
                        },
                      }}>
                      {q.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </PopupTemplate>
  )
}
